import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Heart, Inbox, Send, Eye } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [stats, setStats] = useState({ sent: 0, received: 0, matches: 0, views: 0 });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } catch {}
    })();
  }, []);

  const tiles = [
    { label: t("dash.sent"), value: stats.sent, icon: <Send className="h-4 w-4" strokeWidth={1.5}/>, to: "/interests", testid: "stat-sent" },
    { label: t("dash.received"), value: stats.received, icon: <Inbox className="h-4 w-4" strokeWidth={1.5}/>, to: "/interests", testid: "stat-received" },
    { label: t("dash.matchesLabel"), value: stats.matches, icon: <Heart className="h-4 w-4" strokeWidth={1.5}/>, to: "/matches", testid: "stat-matches" },
    { label: t("dash.views"), value: stats.views, icon: <Eye className="h-4 w-4" strokeWidth={1.5}/>, to: "/profile/me", testid: "stat-views" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="dashboard-page">
      <p className="overline mb-3">{t("dash.kicker")}</p>
      <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">{t("dash.hello")}, {user?.full_name?.split(" ")[0]}.</h1>
      {!user?.profile_complete && (
        <div className="mt-8 p-6 rounded-2xl bg-[#F2EFE9] border border-[#E2DCD0] flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="incomplete-banner">
          <div>
            <h3 className="font-serif-display text-xl mb-1">{t("dash.incomplete.title")}</h3>
            <p className="text-sm text-[#5d574f]">{t("dash.incomplete.body")}</p>
          </div>
          <Link to="/profile/me" data-testid="complete-profile-link" className="rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-5 py-2.5 text-sm transition">{t("dash.complete")}</Link>
        </div>
      )}

      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        {tiles.map((tile) => (
          <Link key={tile.label} to={tile.to} data-testid={tile.testid}
            className="lift-on-hover block p-6 rounded-2xl bg-white border border-[#E2DCD0]">
            <div className="text-[#C85A44] mb-4">{tile.icon}</div>
            <div className="font-serif-display text-4xl mb-1">{tile.value}</div>
            <div className="text-xs uppercase tracking-widest text-[#8C857B]">{tile.label}</div>
          </Link>
        ))}
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-6">
        <Link to="/browse" data-testid="dashboard-browse-cta" className="p-10 rounded-3xl bg-[#2C2A28] text-white lift-on-hover block">
          <p className="overline text-white/60 mb-3">{t("dash.todayKicker")}</p>
          <h3 className="font-serif-display text-3xl mb-3">{t("dash.todayTitle")}</h3>
          <p className="text-sm text-white/70">{t("dash.todayBody")}</p>
        </Link>
        <Link to="/matches" data-testid="dashboard-matches-cta" className="p-10 rounded-3xl bg-[#C85A44] text-white lift-on-hover block">
          <p className="overline text-white/60 mb-3">{t("dash.convKicker")}</p>
          <h3 className="font-serif-display text-3xl mb-3">{t("dash.convTitle")}</h3>
          <p className="text-sm text-white/80">{t("dash.convBody")}</p>
        </Link>
      </div>
    </div>
  );
}
